// One customer at a glance, beside the list (or as a sheet on narrower screens):
// what changed, their usual pattern, what they stopped buying, what similar customers
// buy, this season, recent orders and how to reach them.
import { useMemo, useRef } from "react";
import { getCustomerDetail } from "../../distflowAPI/salesAnalyticsApi";
import { cadence, daysAgo, phoneText, plural, whyText } from "./customerText";
import { count, money, pct, shortDate, shortRange, signedPct, tone } from "./format";
import DetailPanel, { PanelHead } from "./DetailPanel";
import useSideReport from "./useSideReport";

function Status({ data }) {
  const pattern = data.pattern;
  if (!pattern) {
    return <div className="sa-panel-status">
      <p><strong>No orders in 16 weeks.</strong> {data.last_order_date ? `Last order ${shortDate(data.last_order_date, { year: true })}, ${daysAgo(data.days_since_last_order)}.` : "No orders on record."}</p>
    </div>;
  }
  if (pattern.status) {
    const why = whyText({ ...pattern, last_order_date: pattern.last_order_date, days_since_last_order: pattern.days_since_last_order });
    const others = pattern.reasons.filter((reason) => reason.type !== pattern.status).map((reason) => reason.detail);
    return <div className="sa-panel-status">
      <p><strong>{why.title}.</strong> {why.detail}{others.length ? ` ${others.join(". ")}.` : ""}</p>
    </div>;
  }
  return <div className="sa-panel-status">
    <p><strong>{pattern.regular ? "Buying as usual." : "Not a regular yet."}</strong> {pattern.regular
      ? `Orders ${cadence(pattern.typical_gap_days)}, about ${money(pattern.baseline_weekly)} a week.`
      : "Too few orders in the 12 weeks before the last 4 to judge a usual pattern."}</p>
  </div>;
}

function WeeklyBars({ weeks, recentTone }) {
  const max = Math.max(1, ...weeks.map((week) => Number(week.sales)));
  return <figure className="sa-panel-weeks">
    <figcaption>Last 16 weeks</figcaption>
    <div className="sa-panel-bars" aria-hidden="true">
      {weeks.map((week, index) => <span key={week.end_date} title={`${shortRange(week.start_date, week.end_date)}: ${money(week.sales)}`}
        className={index >= weeks.length - 4 ? `is-recent is-${recentTone}` : ""} style={{ height: `${Math.max(2, (Number(week.sales) / max) * 100)}%` }} />)}
    </div>
    <div className="sa-panel-bars-axis" aria-hidden="true"><span>{shortDate(weeks[0]?.start_date)}</span><span>Last 4 weeks</span></div>
    <table className="sa-visually-hidden"><caption>Weekly sales</caption><tbody>
      {weeks.map((week) => <tr key={week.end_date}><th scope="row">{shortRange(week.start_date, week.end_date)}</th><td>{money(week.sales)}</td></tr>)}
    </tbody></table>
  </figure>;
}

function Season({ season }) {
  if (!season) return null;
  const year = `${season.label} ${season.hebrew_year}`;
  if (season.status === "not_started") {
    return <p>{year} starts {shortDate(season.holiday_start)}. Last season they bought {money(season.last_season_total)}.</p>;
  }
  const change = season.sales_change_percent;
  return <p>{year}{season.status === "in_progress" ? " so far" : ""}: <strong>{money(season.sales)}</strong>
    {change != null && <> <span className={`sa-delta sa-delta--${tone(change)}`}>{signedPct(change)}</span></>}
    {" "}against {money(season.previous_sales)} at the same point last year.</p>;
}

export function CustomerDetail({ data, loading, failed, onClose, onFocus, headingRef }) {
  if (!data) {
    return <div className="sa-panel-body">
      <PanelHead title={failed ? "Customer details" : "Loading customer…"} onClose={onClose} closeLabel="Close customer details" headingRef={headingRef} />
      <p className="sa-panel-muted">{failed ? "These details could not be loaded." : loading ? "Looking up their orders…" : ""}</p>
    </div>;
  }
  const pattern = data.pattern;
  const dropped = [...(pattern?.dropped_categories || [])];
  const droppedProducts = pattern?.dropped_products || [];
  const subtitle = [data.account_type, data.territories?.join(", "), data.salesman?.name || "No salesman"].filter(Boolean).join(", ");
  const recentTone = pattern && Number(pattern.weekly_change_percent) <= -25 ? "down" : "steady";
  const contacts = data.accounts.filter((account) => account.phones.length || account.address).slice(0, 3);
  const link = data.link.type === "customer" ? `/customers/${data.link.id}` : `/accounts/${data.link.id}`;

  return <div className="sa-panel-body">
    <PanelHead title={data.name} subtitle={subtitle} onClose={onClose} closeLabel="Close customer details" headingRef={headingRef} />

    <Status data={data} />

    {pattern && <dl className="sa-panel-stats">
      <div><dt>Usually</dt><dd>{money(pattern.baseline_weekly)} a week</dd></div>
      <div><dt>Last 4 weeks</dt><dd>{money(pattern.recent_weekly)} a week
        {pattern.weekly_change_percent != null && <small className={`sa-delta sa-delta--${tone(pattern.weekly_change_percent)}`}>{signedPct(pattern.weekly_change_percent, 0)}</small>}</dd></div>
      <div><dt>Orders</dt><dd>{cadence(pattern.typical_gap_days) || "—"}<small>{pattern.recent_orders} in the last 4 weeks</small></dd></div>
      <div><dt>Last order</dt><dd>{shortDate(pattern.last_order_date)}<small>{daysAgo(pattern.days_since_last_order)}</small></dd></div>
    </dl>}

    {data.weekly?.length > 0 && <WeeklyBars weeks={data.weekly} recentTone={recentTone} />}

    {(dropped.length > 0 || droppedProducts.length > 0) && <section className="sa-panel-section" aria-label="Stopped or cut back">
      <h3>Stopped or cut back</h3>
      <ul className="sa-panel-list">
        {dropped.map((item) => <li key={`c${item.id}`}><span><strong>{item.name}</strong>{item.status === "stopped" ? `Last bought ${shortDate(item.last_bought)}` : "Cut back"}. Was {money(item.baseline_weekly)} a week.</span>
          <span>{money(item.recent_weekly)} a week</span></li>)}
      </ul>
      {droppedProducts.length > 0 && <p className="sa-panel-muted">Stopped: {droppedProducts.map((item) => item.name).join(", ")}</p>}
    </section>}

    {data.suggestions?.rows?.length > 0 && <section className="sa-panel-section" aria-label="What similar customers buy">
      <h3>Other {data.suggestions.account_type || "similar"} customers also buy</h3>
      <ul className="sa-panel-list">
        {data.suggestions.rows.map((item) => <li key={item.key}><span><strong>{item.name}</strong>{item.category}</span>
          <span>{pct(item.share_percent, 0)} of {count(data.suggestions.peers)} {plural(data.suggestions.peers, "customer")}</span></li>)}
      </ul>
    </section>}

    {data.season && <section className="sa-panel-section" aria-label="This season"><h3>Yom Tov season</h3><Season season={data.season} /></section>}

    {data.recent_orders?.length > 0 && <section className="sa-panel-section" aria-label="Recent orders">
      <h3>Recent orders</h3>
      <ul className="sa-panel-list">{data.recent_orders.map((order) => <li key={order.id}>
        <span>{shortDate(order.date, { year: true })}{data.accounts.length > 1 ? `, ${order.account}` : ""}</span><span>{money(order.total, { cents: true })}</span>
      </li>)}</ul>
    </section>}

    {contacts.length > 0 && <section className="sa-panel-section" aria-label="Contact">
      <h3>Contact</h3>
      {contacts.map((account) => <div className="sa-panel-contact" key={account.id}>
        {data.accounts.length > 1 && <strong>{account.name}</strong>}
        {account.address && <span>{account.address}</span>}
        {account.phones.map((entry) => <a key={entry.phone} href={`tel:${entry.phone}`}>{phoneText(entry.phone)}{entry.note ? ` (${entry.note})` : ""}</a>)}
      </div>)}
    </section>}

    <div className="sa-panel-actions">
      <a className="is-primary" href={link} target="_blank" rel="noreferrer">Open the {data.link.type}</a>
      <button type="button" onClick={() => onFocus?.({ key: data.key, name: data.name })}>Show the page for this customer</button>
    </div>
  </div>;
}

export default function CustomerPanel({ customerKey, asOf, mode = "sheet", onClose, onFocus }) {
  const params = useMemo(() => ({ key: customerKey, as_of: asOf }), [customerKey, asOf]);
  const detail = useSideReport(getCustomerDetail, params, Boolean(customerKey && asOf));
  const headingRef = useRef(null);

  return <DetailPanel mode={mode} label="Customer details" busy={detail.loading} onClose={onClose}>
    <CustomerDetail data={detail.data} loading={detail.loading} failed={detail.failed} onClose={onClose} onFocus={onFocus} headingRef={headingRef} />
  </DetailPanel>;
}
